import { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import api from "../../services/api";
import IncomingCallBanner, { callerDisplayName } from "../messenger/IncomingCallBanner";
import { showToast } from "../../utils/toast";
import { messengerPathForRole, isMessengerRoute } from "../../utils/messengerPaths";
import {
  filterActiveIncomingCalls,
  isDismissedCallSession,
  markDismissedCallSession,
  stashActiveCallPayload,
} from "../../utils/messengerIncoming";
import "./AppBottomDock.css";

const MESSENGER_ROLES = new Set(["CARE_MANAGER", "ADMIN"]);
const POLL_MS = 8000;

function MessengerFab({ hasIncomingCall, onClick }) {
  return (
    <button
      type="button"
      className={`app-bottom-dock__fab ${hasIncomingCall ? "app-bottom-dock__fab--ringing" : ""}`}
      aria-label={hasIncomingCall ? "Open messenger — incoming call" : "Open messenger"}
      title="Messenger"
      onClick={onClick}
    >
      {hasIncomingCall ? <span className="app-bottom-dock__fab-pulse" aria-hidden /> : null}
      <svg className="app-bottom-dock__fab-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.85" aria-hidden>
        <path d="M4 5h16v11H7l-3 3V5z" strokeLinejoin="round" />
      </svg>
    </button>
  );
}

const DISCONNECTED_NOTICE_MS = 3500;

function disconnectedMessage(reason) {
  if (reason === "declined") return "Call declined";
  return "Disconnected";
}

export default function AppBottomDock() {
  const location = useLocation();
  const navigate = useNavigate();
  const role = api.getAuthRoleFromToken?.() || "";
  const [incomingCall, setIncomingCall] = useState(null);
  const [answerBusy, setAnswerBusy] = useState(false);
  const [contacts, setContacts] = useState([]);
  const [disconnectedNotice, setDisconnectedNotice] = useState("");

  const openMessenger = useCallback(() => {
    navigate(messengerPathForRole(role));
  }, [navigate, role]);

  const pollIncoming = useCallback(async () => {
    if (!MESSENGER_ROLES.has(role)) return;
    try {
      const res = await api.getMessengerIncomingCalls();
      const calls = filterActiveIncomingCalls(res?.calls || res?.data?.calls || []);
      const ringing = calls[0];
      if (ringing && !isDismissedCallSession(ringing.id)) {
        setIncomingCall(ringing);
      } else {
        setIncomingCall(null);
      }
    } catch {
      // RTC may be disabled or user not authenticated
    }
  }, [role]);

  useEffect(() => {
    if (!MESSENGER_ROLES.has(role)) {
      setContacts([]);
      return undefined;
    }
    api
      .getMessengerContacts()
      .then((res) => {
        setContacts(res?.contacts || res?.data?.contacts || []);
      })
      .catch(() => {});
    return undefined;
  }, [role]);

  useEffect(() => {
    if (!MESSENGER_ROLES.has(role)) {
      setIncomingCall(null);
      return undefined;
    }

    pollIncoming();
    const intervalMs = incomingCall ? 2000 : POLL_MS;
    const timer = setInterval(pollIncoming, intervalMs);
    return () => clearInterval(timer);
  }, [incomingCall, pollIncoming, role]);

  function declineIncoming() {
    if (!incomingCall?.id) return;
    const sessionId = incomingCall.id;
    markDismissedCallSession(sessionId);
    setIncomingCall(null);
    sessionStorage.removeItem("nema_pending_incoming_call");
    api.declineMessengerCall(sessionId).catch(() => {});
  }

  async function answerIncoming() {
    const call = incomingCall;
    if (!call?.id || answerBusy) return;
    setAnswerBusy(true);
    try {
      const res = await api.answerMessengerCall(call.id);
      const rtc = res?.rtc || res?.data?.rtc;
      if (!rtc?.token) {
        throw new Error("Call is no longer available");
      }
      markDismissedCallSession(call.id);
      setIncomingCall(null);
      const payload = {
        open: true,
        mode: "active",
        session: call,
        peer: {
          userId: call.callerUserId,
          displayName: callerDisplayName(call, contacts),
        },
        callType: call.callType || "video",
        rtcCredentials: rtc,
      };
      stashActiveCallPayload(payload);
      if (isMessengerRoute(location.pathname)) {
        window.dispatchEvent(new CustomEvent("nema:messenger-active-call", { detail: payload }));
      } else {
        openMessenger();
      }
    } catch (err) {
      showToast.error(err?.message || "Unable to answer call.");
      pollIncoming();
    } finally {
      setAnswerBusy(false);
    }
  }

  useEffect(() => {
    function onCallEnded(e) {
      setIncomingCall(null);
      const detail = e?.detail || {};
      setDisconnectedNotice(disconnectedMessage(detail.reason));
    }
    window.addEventListener("nema:messenger-call-ended", onCallEnded);
    return () => window.removeEventListener("nema:messenger-call-ended", onCallEnded);
  }, []);

  useEffect(() => {
    if (!disconnectedNotice) return undefined;
    const timer = window.setTimeout(() => setDisconnectedNotice(""), DISCONNECTED_NOTICE_MS);
    return () => window.clearTimeout(timer);
  }, [disconnectedNotice]);

  if (!MESSENGER_ROLES.has(role)) return null;

  const onMessengerPage = isMessengerRoute(location.pathname);

  return (
    <>
      {incomingCall ? (
        <IncomingCallBanner
          call={incomingCall}
          contacts={contacts}
          busy={answerBusy}
          className="incoming-call-banner--dock"
          onAnswer={answerIncoming}
          onDecline={declineIncoming}
        />
      ) : disconnectedNotice ? (
        <IncomingCallBanner
          variant="disconnected"
          message={disconnectedNotice}
          className="incoming-call-banner--dock"
        />
      ) : onMessengerPage ? null : (
        <div className="app-bottom-dock" role="region" aria-label="Quick actions">
          <MessengerFab hasIncomingCall={false} onClick={openMessenger} />
        </div>
      )}
    </>
  );
}
